// React
import { useEffect, useState } from "react";

// MUI
import { Grid } from "@mui/material";

// Third party
import moment from "moment";

// Helpers
import { formatSideMenu, getCurrentCommissionCycle } from "@/Common/helpers/actions";

// Components
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";

// Hooks
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import { useCommissionCyclesTree } from "@/Washouts/hooks/common/useCommissionCyclesTree";
import { useCallbackState } from "@Common/hooks/CallbackState";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

/************************************************ HELPERS ************************************************/
const sideMenu = (list, year, element) => formatSideMenu(list, year, element);

const getCommissionCycleTree = (list = []) => {
	let tree = [];
	let expanded = [];
	let defaultCommissionCycle = null;

	list?.forEach((element) => {
		const year = moment(element.startDate).utc().format(DATE_FORMAT.yearOnly);
		tree = sideMenu(tree, year, {
			...element,
			year,
		});
	});

	if (tree.length) {
		defaultCommissionCycle = getCurrentCommissionCycle(list);
		if (defaultCommissionCycle) {
			const year = moment(defaultCommissionCycle.startDate).utc().format(DATE_FORMAT.yearOnly);
			expanded = [year];
		}
	}

	return {
		tree,
		defaultCommissionCycle,
		expanded,
	};
};

/**
 * Legacy Commission Cycle Tree
 * @description This component is used to display the Commission Cycle tree in the SideBar using legacy data
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function LegacyCommissionCycleTree(props) {
	const { selected, selectBy = "_id", disabled = false, callbackSelected, selectDefaultLastCommissionCycle } = props;

	const { isLoading, locationId, allCommissionCycles: treeData } = useCommissionCyclesData();

	const [tree, setTree] = useState([]);
	const [selectedCycle, setSelectedCycle] = useState(null);
	const [expanded, setExpanded] = useState([]);

	const handleSelection = (element) => {
		if (element) {
			setSelectedCycle(element);
			const actualCycle = Boolean(element?.status === "ACTIVE" || element?.status === "CLOSING" || element?.status === "POSTED");
			callbackSelected(element, actualCycle);
		}
	};

	useEffect(() => {
		let result = null;

		if (treeData) {
			result = getCommissionCycleTree(treeData);
			setTree(result.tree);
		}

		// Looking for current Commission Cycle
		if (result && selectDefaultLastCommissionCycle && !Boolean(selected)) {
			try {
				setSelectedCycle(result.defaultCommissionCycle);
				setExpanded(result.expanded);

				if (callbackSelected) {
					const isActualCycle = Boolean(
						result.defaultCommissionCycle?.status === "ACTIVE" ||
							result.defaultCommissionCycle?.status === "CLOSING" ||
							result.defaultCommissionCycle?.status === "POSTED"
					);
					callbackSelected(result.defaultCommissionCycle, isActualCycle);
				}
			} catch (error) {
				console.log(error);
			}
		} else if (typeof selected !== "undefined" && selected) {
			setSelectedCycle(selected);
			const selectedYear = moment(selected.startDate).utc().format(DATE_FORMAT.yearOnly);
			setExpanded([selectedYear]);
		}

		return () => {
			setSelectedCycle(null);
			setExpanded([]);
		};
	}, [treeData, selected, selectDefaultLastCommissionCycle, callbackSelected]);

	return (
		<AppSideBar
			searchProps={{
				loading: isLoading,
			}}
			sideBarProps={{
				tree,
				expanded,
				loading: isLoading,
				selected: selectedCycle?._id ?? "",
				onSelectAction: handleSelection,
			}}
		/>
	);
}

function GenericCommissionCycleTree(props) {
	const { callbackSelected } = props;

	const { data, isLoading } = useCommissionCyclesTree();
	const [selectedCycle, setSelectedCycle] = useCallbackState(null, callbackSelected);

	return (
		<AppSideBar
			searchProps={{ loading: isLoading }}
			sideBarProps={{
				tree: data?.tree,
				loading: isLoading,
				selected: selectedCycle?._id ?? "",
				onSelectAction: setSelectedCycle,
			}}
		/>
	);
}

export default function CommissionSideBar(props) {
	const { enableGenericTree = false } = props;

	return (
		<Grid>
			{enableGenericTree ? (
				<GenericCommissionCycleTree {...props} />
			) : (
				<LegacyCommissionCycleTree {...props} />
			)}
		</Grid>
	);
}
